import React, { useState, useCallback } from 'react';
import styled, { css } from 'styled-components';
import Button from '~/components/atom/Button';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Heading4, Variant } from '~/components/atom/Typography';
import { ZapierFieldType } from '~/graphql/types';
import FieldEditingSection, {
  type OnChangeValue,
} from '../FieldEditingSection';
import { v4 as uuidv4 } from 'uuid';
import TEST_ID from './index.testid';
import alterObjectById from '~/util/alterObjectById';
import type { ErrorObj, ZapierFieldsFE } from '../../types';

const text = {
  heading: 'Velden',
  newFieldButton: 'Nieuw veld toevoegen',
  emptyInputError: 'Het veld kan niet leeg zijn',
};

export type Props = {
  fields?: ZapierFieldsFE;
  onFieldsChange: (fields: ZapierFieldsFE) => void;
  errors: { [id: string]: ErrorObj };
};

const FieldsContainer: React.FCC<Props> = ({
  dataTestId,
  fields = [],
  onFieldsChange,
  errors,
  ...rest
}) => {
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);

  const onActiveField = useCallback(
    (id: string | null, value: OnChangeValue) => {
      if (!id) return;
      const updated = alterObjectById(id, value, fields);
      onFieldsChange(updated);
    },
    [fields, onFieldsChange],
  );

  const onNewField = useCallback(() => {
    const id = uuidv4();

    onFieldsChange([
      ...fields,
      {
        __typename: 'ZapierField',
        key: `nieuwe_variabele_${fields.length + 1}`,
        label: `Nieuwe variabele_${fields.length + 1}`,
        type: ZapierFieldType.String,
        defaultString: null,
        defaultNumber: null,
        defaultBoolean: null,
        id,
      },
    ]);

    setActiveFieldId(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  const activeFieldPredicate = field => field.id === activeFieldId;
  const activeFieldIndex = fields.findIndex(activeFieldPredicate);

  const fieldHasErrors = (id: string | undefined): boolean => {
    if (!id) return false;
    const fieldErrors = errors[id] ?? null;
    if (!fieldErrors) return false;
    return Object.values(fieldErrors).some(value => value && value.length > 0);
  };

  return (
    <>
      <Heading4 variant={Variant.primary}>{text.heading}</Heading4>
      <Container justification="center" data-testid={dataTestId} {...rest}>
        <FieldsWrapper>
          <StyledButton
            label={text.newFieldButton}
            icon="plus"
            appearance="secondary"
            size="medium"
            onClick={onNewField}
            dataTestId={TEST_ID.NEW_FIELD_BUTTON}
          />
          {fields.map(({ label, id }, index) => (
            <FieldLabel
              data-testid={TEST_ID.FIELDS_LIST}
              data-objectid={`field-${index}`}
              key={id}
              $isActive={id === activeFieldId}
              onClick={() => id && setActiveFieldId(id)}
              $hasError={fieldHasErrors(id)}
            >
              {label || text.emptyInputError}
            </FieldLabel>
          ))}
        </FieldsWrapper>

        <FieldEditingSection
          onChange={newValue => onActiveField(activeFieldId, newValue)}
          field={fields[activeFieldIndex]}
          errors={activeFieldId ? errors[activeFieldId] : undefined}
          onDelete={() =>
            onFieldsChange(fields.filter(field => field.id !== activeFieldId))
          }
          onClose={() => setActiveFieldId(null)}
        />
      </Container>
    </>
  );
};

const Container = styled(JustificationContainer)<{}>`
  min-height: 400px;

  ${({ theme }) => css`
    padding: ${theme.space('base')};
    background-color: ${theme.color('primary', 'translucent')};
    border-radius: ${theme.getTokens().border.radius.base};
  `}
`;

const StyledButton = styled(Button)<{}>`
  display: flex;
  justify-content: start;
`;

const FieldsWrapper = styled.div<{}>`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const FieldLabel = styled.div<{
  $hasError: boolean;
  $isActive: boolean;
}>`
  cursor: pointer;

  ${({ theme, $hasError, $isActive }) => css`
    color: ${theme.color(
      $hasError ? 'danger' : $isActive ? 'white' : 'primary',
    )};
    padding: ${theme.space('base')};
    border-radius: ${theme.getTokens().border.radius.base};
    background-color: ${$isActive ? theme.color('primary') : 'none'};
    margin-top: ${theme.space('xxs')};

    &:hover {
      color: ${theme.color($hasError ? 'danger' : 'white')};
      background-color: ${theme.color('primary')};
    }
  `}
`;

export default FieldsContainer;
